/**
 * Admin Layout Route
 * Layout for admin pages with sidebar navigation
 */

import { createFileRoute, Link, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { Settings, FileQuestion, Shield, KeyRound, Home, Users, UsersRound, UserRoundCheck, FlaskConical, Bell, BarChart3, Flag, UploadCloud, LineChart, Ticket, QrCode, Tags, Globe2, GraduationCap, ClipboardList, ListChecks, Sparkles, BookOpen, Handshake, Zap, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIsSalesperson, useIsSuperAdmin, useIsSupportAdmin } from '@/api/hooks';
import { AccessRestricted } from '@/components/admin/AccessRestricted';
import { Loader2 } from 'lucide-react';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { NavIconPanelLeft } from '@/components/icons/NavIcons';
import { HorizontalPillRail } from '@/components/shared/HorizontalPillRail';
import { useAccessRequestsPendingCount } from '@/components/admin/access-requests/access-requests-data';
import {
  AVOID_GAP_PX,
  DESKTOP_INSET_PX,
  LAUNCHER_SIZE_PX,
} from '@/components/alfie-widget/AlfieLauncher';
import { isDevEnvironment } from '@/lib/env';

const SIDEBAR_KEY = 'admin-sidebar-expanded';

/**
 * The floating Alfie launcher's column (88px): its inset from the right edge,
 * the disc, and the clearance it keeps from anything it avoids. From `md` up
 * (where the launcher uses the desktop inset) the admin scroller reserves this
 * column on its right, so no page content ever scrolls under the disc - the
 * last column of a table included, at any scroll position, not only at the
 * end of the page. Exposed as `--admin-launcher-gutter` so a full-bleed page
 * header can extend into the column while keeping its own content out of it.
 */
const LAUNCHER_GUTTER_PX = DESKTOP_INSET_PX + LAUNCHER_SIZE_PX + AVOID_GAP_PX;

interface AdminNavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Pages a support_admin can use - everything else stays super-admin only. */
  support?: boolean;
  /**
   * Pages a salesperson (sales_deals:write) can use. Only Sales: the page shows them their
   * own sales through the hermes `sales-desk` function, never the super-admin view.
   */
  sales?: boolean;
  devOnly?: boolean;
  /**
   * This item carries a count badge. Only the access-requests queue does: it is
   * the one admin page whose depth has to be visible from every other page, and
   * it is the reason the queue stopped being a tab inside /admin/users.
   */
  badge?: 'access_requests';
  /** Shown only while this returns true (a feature flag). */
  flag?: () => boolean;
}

const NAV_ITEMS: AdminNavEntry[] = [
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  // Next to Analytics, its read-only reporting peer, and deliberately NOT
  // support: true - it reports spend across every organisation, and the
  // admin_ai_usage RPC raises 42501 for anything but a super_admin anyway.
  { to: '/admin/ai-usage', label: 'AI usage', icon: Sparkles },
  { to: '/admin/revenue', label: 'Revenue', icon: LineChart, support: true },
  { to: '/admin/users', label: 'Users', icon: UsersRound, support: true },
  // Directly under Users because that is where masquerading starts, and this is
  // what masquerading requires. Your OWN authenticator, never a view of anyone
  // else's — the enrol and verify calls only ever act on the caller's session.
  // `support: true` because it is their own account security, even though
  // masquerade itself stays super-admin only.
  { to: '/admin/security', label: 'Your authenticator', icon: KeyRound, support: true },
  // Next to Users: same job at a different stage - Users is who is in,
  // this is who is asking to get in. Listing requests already accepts
  // billing/admin, so a support_admin can read it (deciding stays narrower).
  {
    to: '/admin/access-requests',
    label: 'Access requests',
    icon: UserRoundCheck,
    support: true,
    badge: 'access_requests',
  },
  { to: '/admin/event-purchases', label: 'Event Purchases', icon: Ticket },
  { to: '/admin/diagnostic/questions', label: 'Questions', icon: FileQuestion },
  { to: '/admin/onboarding-questions', label: 'Onboarding', icon: ListChecks },
  { to: '/admin/diagnostic/guardrails', label: 'Guardrails', icon: Shield },
  { to: '/admin/import-sync', label: 'Clickup sync', icon: UploadCloud, devOnly: true },
  { to: '/admin/diagnostic/inspect', label: 'Inspector', icon: FlaskConical },
  { to: '/admin/diagnostic/settings', label: 'Settings', icon: Settings },
  { to: '/admin/avatar-check', label: 'Avatars', icon: Users },
  { to: '/admin/mentorship', label: 'Mentorship', icon: GraduationCap },
  { to: '/admin/tier-settings', label: 'Tier Settings', icon: Tags },
  { to: '/admin/communities', label: 'Communities', icon: Globe2 },
  { to: '/admin/signup-codes', label: 'Signup Codes', icon: QrCode },
  { to: '/admin/free-courses', label: 'Free courses', icon: BookOpen },
  { to: '/admin/sales', label: 'Sales', icon: Handshake, sales: true },
  // Deliberately NOT support: true - member sentiment is not a support surface.
  { to: '/admin/surveys', label: 'Surveys', icon: ClipboardList },
  // Behind the speedies flag (filtered below). Super admins only, like Surveys.
  { to: '/admin/speedies', label: 'Speedies', icon: Zap, flag: isSpeediesEnabled },
  { to: '/admin/push-diagnostics', label: 'Push', icon: Bell },
  { to: '/admin/flags', label: 'Feature Flags', icon: Flag },
];

function salesCanOpen(pathname: string): boolean {
  return NAV_ITEMS.some(
    (item) => item.sales && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
  );
}

function supportCanOpen(pathname: string): boolean {
  if (pathname === '/admin' || pathname === '/admin/') return true;
  return NAV_ITEMS.some(
    (item) => item.support && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
  );
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const { isSuperAdmin, isLoading: superLoading } = useIsSuperAdmin();
  const { isSupportAdmin, isLoading: supportLoading } = useIsSupportAdmin();
  const { isSalesperson, isLoading: salesLoading } = useIsSalesperson();
  const { pathname } = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => localStorage.getItem(SIDEBAR_KEY) !== 'false',
  );

  // One cheap shared query for the whole shell (a bare count(*), no rows), so
  // the badge is fetched once and does not refetch as you move between admin
  // pages. The queue page publishes its own pending total into the same cache
  // key, so approving the last row empties the badge with no second request.
  //
  // Called BEFORE the loading and access-denied returns below - every hook in
  // this component has to run on every render, and the role checks resolve
  // asynchronously. `enabled` carries the gate instead: a role that cannot read
  // the queue never fires the request.
  const pendingRequests = useAccessRequestsPendingCount(isSuperAdmin || isSupportAdmin);
  const badgeFor = (item: AdminNavEntry): number | null => {
    if (item.badge !== 'access_requests') return null;
    const n = pendingRequests.data ?? 0;
    // Zero is not a badge - an empty queue should read as empty, not as a "0".
    return n > 0 ? n : null;
  };

  const toggleSidebar = () => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  };

  if (superLoading || supportLoading || salesLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSuperAdmin && !isSupportAdmin && !isSalesperson) {
    return (
      <div className="flex min-h-0 flex-1 bg-background">
        <AccessRestricted message="Super admin or support admin role required." />
      </div>
    );
  }

  // A salesperson only has the Sales page: the bare /admin address goes straight there.
  if (!isSuperAdmin && !isSupportAdmin && (pathname === '/admin' || pathname === '/admin/')) {
    return <Navigate to="/admin/sales" replace />;
  }

  // Support admins get the billing pages only (the admin-billing edge fn enforces the
  // same scope server-side); salespeople get Sales only. Roles add up: a support admin
  // who also sells sees both sets of pages.
  const roleAllows = (item: AdminNavEntry): boolean =>
    isSuperAdmin || (isSupportAdmin && !!item.support) || (isSalesperson && !!item.sales);
  const visibleItems = NAV_ITEMS.filter(
    (item) => (isDevEnvironment || !item.devOnly) && roleAllows(item) && (!item.flag || item.flag()),
  );
  const canOpen = (path: string): boolean =>
    isSuperAdmin || (isSupportAdmin && supportCanOpen(path)) || (isSalesperson && salesCanOpen(path));

  return (
    /*
     * `flex-1 min-h-0`, NOT `h-full`.
     *
     * The app content area is a scrolling flex COLUMN, and the shell is only one
     * of its children - a notification prompt or any other app-level banner
     * renders as a sibling ABOVE it. `h-full` there means 100% of the column's
     * content box, so the shell claimed the whole height while a 90px banner had
     * already taken part of it: the column overflowed by exactly the banner
     * height, and scrolling to the bottom of a long page (revenue, the member
     * record) ran past the end of the sidebar into a void of page background.
     * Filling the REMAINING space instead is correct at any banner height, and
     * the nav inside already has its own `overflow-y-auto` for when it is taller
     * than what is left.
     */
    <div className="flex min-h-0 flex-1 bg-background">
      {/* Sidebar - desktop/tablet only; below md the pill rail in <main> takes over */}
      <aside
        className={`${sidebarExpanded ? 'w-64' : 'w-14'} border-r border-border bg-card flex-shrink-0 hidden md:flex flex-col transition-all duration-200`}
      >
        {/* Toggle button row */}
        <div className={`flex items-center border-b border-border h-14 ${sidebarExpanded ? 'px-4 justify-between' : 'justify-center'}`}>
          {sidebarExpanded && (
            <div>
              <p className="text-sm font-semibold leading-tight">Admin Panel</p>
              <p className="text-xs text-muted-foreground">Diagnostic Management</p>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            title={sidebarExpanded ? 'Collapse' : 'Expand'}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex-shrink-0"
          >
            <NavIconPanelLeft
              className={`w-[18px] h-[18px] transition-transform duration-200 ${sidebarExpanded ? '' : 'rotate-180'}`}
            />
          </button>
        </div>

        {/* `min-h-0` is load-bearing: a flex child defaults to min-height:auto and
            refuses to shrink below its content, so `overflow-y-auto` alone would
            never scroll - the list would simply clip off the bottom of the aside. */}
        <nav className="p-2 space-y-1 flex-1 min-h-0 overflow-y-auto">
          <NavItem to="/" icon={<Home className="w-4 h-4 flex-shrink-0" />} label="Back to Home" expanded={sidebarExpanded} />
          {visibleItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={<item.icon className="w-4 h-4 flex-shrink-0" />}
              label={item.label}
              expanded={sidebarExpanded}
              badge={badgeFor(item)}
            />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Mobile nav - horizontally scrollable pill rail replacing the sidebar below md */}
        <div className="md:hidden flex-shrink-0 border-b border-border px-3 py-2">
          <HorizontalPillRail ariaLabel="Admin navigation" resetKey={visibleItems.length}>
            <div className="nav-pill-container flex-shrink-0">
              <MobileNavPill to="/" icon={Home} label="Home" active={false} />
              {visibleItems.map((item) => (
                <MobileNavPill
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  active={pathname === item.to || pathname.startsWith(`${item.to}/`)}
                  badge={badgeFor(item)}
                />
              ))}
            </div>
          </HorizontalPillRail>
        </div>

        {/* The admin shell is full-bleed (Layout applies no padding or scroll
            on /admin), so THIS is the page's scroll container - the sidebar
            stays pinned while only the content scrolls. Padding matches the
            layout wrapper other routes get, except on the right. */}
        {/* The floating Alfie launcher sits over the bottom-right corner.
            - From `md` up the right padding IS the launcher's column
              (LAUNCHER_GUTTER_PX), so content never passes under the disc and
              the bottom needs no more than the normal padding.
            - Below `md` a phone cannot spare 84px of its width, so pb-24 keeps
              the old contract instead: the last row, menu or pagination control
              can always scroll clear of the disc. */}
        <div
          className="flex-1 min-h-0 overflow-y-auto py-4 pb-24 pl-3 pr-3 sm:pl-4 sm:pr-4 md:pb-4 md:pr-[var(--admin-launcher-gutter)] lg:pl-6"
          style={{ '--admin-launcher-gutter': `${LAUNCHER_GUTTER_PX}px` } as React.CSSProperties}
        >
          {!canOpen(pathname) ? (
            <AccessRestricted message="This page requires the super admin role." />
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}

interface MobileNavPillProps {
  to: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  badge?: number | null;
}

function MobileNavPill({ to, icon: Icon, label, active, badge }: MobileNavPillProps) {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (active) {
      ref.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  }, [active]);

  return (
    <Link
      ref={ref}
      to={to}
      className={`nav-pill min-h-[44px] flex-shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 ${
        active ? 'nav-pill--active' : 'nav-pill--inactive'
      }`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {label}
      {badge != null && <NavBadge count={badge} label={label} />}
    </Link>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  expanded: boolean;
  badge?: number | null;
}

function NavItem({ to, icon, label, expanded, badge }: NavItemProps) {
  return (
    <Link
      to={to}
      title={!expanded ? (badge != null ? `${label} — ${badge} waiting` : label) : undefined}
      className={`flex items-center gap-3 rounded-lg text-sm font-medium hover:bg-secondary transition-colors [&.active]:bg-primary [&.active]:text-primary-foreground ${
        expanded ? 'px-4 py-2.5' : 'px-0 py-2.5 justify-center'
      }`}
    >
      {/* Collapsed, there is no room for a number - the icon takes a dot, and
          the count moves to the title. */}
      {badge != null && !expanded ? (
        <span className="relative flex-shrink-0">
          {icon}
          <span
            aria-label={`${badge} waiting in ${label}`}
            className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary [.active_&]:bg-primary-foreground"
          />
        </span>
      ) : (
        icon
      )}
      {expanded && <span>{label}</span>}
      {badge != null && expanded && <NavBadge count={badge} label={label} className="ml-auto" />}
    </Link>
  );
}

/**
 * A count on a nav item. Terra-cotta on the resting item; inside the active
 * item, which is already terra-cotta, it inverts to stay legible.
 */
function NavBadge({
  count,
  label,
  className = '',
}: {
  count: number;
  label: string;
  className?: string;
}) {
  return (
    <span
      aria-label={`${count} waiting in ${label}`}
      className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10.5px] font-extrabold tabular-nums flex-shrink-0 [.active_&]:bg-primary-foreground [.active_&]:text-primary ${className}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
